import { type RefObject, useEffect, useRef } from 'react';

import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

type UseInfiniteVirtualScrollProps<TData> = {
    scrollElementRef: RefObject<HTMLElement | null>;
    data: TData[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    estimateSize?: number | ((index: number) => number);
    overscan?: number;
    status: 'pending' | 'error' | 'success';
    error: Error | null;
};

type UseInfiniteVirtualScrollReturn = {
    virtualItems: VirtualItem[];
    totalSize: number;
    isLoadingMore: boolean;
};

export function useInfiniteVirtualScroll<TData>({
    scrollElementRef,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    estimateSize = 36,
    overscan = 5,
    status,
    error,
}: UseInfiniteVirtualScrollProps<TData>): UseInfiniteVirtualScrollReturn {
    const rowVirtualizer = useVirtualizer({
        count: hasNextPage ? data.length + 1 : data.length,
        getScrollElement: () => scrollElementRef.current,
        estimateSize: typeof estimateSize === 'function' ? estimateSize : () => estimateSize,
        overscan,
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    // Gated on `data.length`, not a latched "near end" flag — a latch blocks forever once the
    // sentinel can't leave view; the length re-arms on growth, `isFetchingNextPage` caps duplicates.
    const fetchedAtLengthRef = useRef<number | null>(null);

    useEffect(() => {
        if (error || status === 'pending') {
            // A failed fetch never grows `data`, so clear the latch — a retry re-arms the guard.
            fetchedAtLengthRef.current = null;
            return;
        }

        const [lastItem] = [...virtualItems].reverse();
        const isNearEnd = !!lastItem && lastItem.index >= data.length - 1;

        if (
            isNearEnd &&
            hasNextPage &&
            !isFetchingNextPage &&
            fetchedAtLengthRef.current !== data.length
        ) {
            fetchedAtLengthRef.current = data.length;
            fetchNextPage();
        }
    }, [hasNextPage, fetchNextPage, data.length, isFetchingNextPage, virtualItems, error, status]);

    return {
        virtualItems,
        totalSize: rowVirtualizer.getTotalSize(),
        isLoadingMore: hasNextPage && isFetchingNextPage,
    };
}
